import { createHmac, randomUUID } from "node:crypto";
import type { Context } from "hono";
import { sql } from "drizzle-orm";

import { redactAuthLogValue } from "./auth-log-redaction.js";

const AUTH_ACTIVITY_SPOOL_DIR = "/var/gencow/platform-data/activity-spool";
const AUTH_ACTIVITY_SECRET_KEY_PATTERN =
  /token|secret|password|passwd|pwd|api[_-]?key|authorization|cookie|session|grant|database[_-]?url/i;
const AUTH_ACTIVITY_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AUTH_ACTIVITY_METADATA_ALLOWLIST = new Set(["reason", "source"]);

type AuthActivityEvent = {
  eventId: string;
  occurredAt: Date;
  receivedAt: Date;
  category: "auth";
  action: string;
  outcome: "success" | "failure" | "denied";
  severity: "info" | "warning";
  actorType: string;
  actorUserId: string | null;
  actorRole: string | null;
  actorSessionIdHash: string | null;
  actorTokenId: string | null;
  targetType: string | null;
  targetId: string | null;
  targetOwnerUserId: string | null;
  appId: number | null;
  appName: string | null;
  environment: string | null;
  deployId: number | null;
  requestId: string | null;
  source: "server.auth";
  ipHash: string | null;
  userAgentHash: string | null;
  country: string | null;
  reasonCode: string | null;
  metadataJson: Record<string, unknown>;
  searchText: string;
  actorUserSnapshotJson: Record<string, unknown>;
  targetUserSnapshotJson: Record<string, unknown>;
  appSnapshotJson: Record<string, unknown>;
  requestContextJson: Record<string, unknown>;
  authAttemptJson: Record<string, unknown>;
  deployContextJson: Record<string, unknown>;
  contextStatus: "full" | "partial" | "anonymous" | "legacy_partial";
  actorEmail: string | null;
  actorEmailHash: string | null;
  targetUserEmail: string | null;
  targetUserEmailHash: string | null;
  attemptedEmail: string | null;
  attemptedEmailHash: string | null;
  ownerEmail: string | null;
  ownerEmailHash: string | null;
  appOwnerUserId: string | null;
  routePath: string | null;
  httpMethod: string | null;
  httpStatus: number | null;
};

type AuthActivityParams = {
  action: string;
  outcome: "success" | "failure" | "denied";
  actorType?: string;
  actorUserId?: string | null;
  actorSessionToken?: string | null;
  reasonCode?: string | null;
  metadata?: Record<string, unknown>;
  authAttempt?: {
    attemptedEmail?: string | null;
    matchedUserId?: string | null;
    provider?: string | null;
  };
  httpStatus?: number | null;
};

type AuthUserSnapshot = {
  id: string;
  email: string;
  name: string | null;
  role: string | null;
  plan: string | null;
  emailVerified: boolean | null;
};

function authActivityHash(value: unknown): string | null {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return null;
  const secret =
    process.env.GENCOW_ACTIVITY_HASH_SECRET ||
    process.env.BETTER_AUTH_SECRET ||
    process.env.GENCOW_PLATFORM_SECRET ||
    "gencow-activity-local-dev-secret";
  return `hmac-sha256:${createHmac("sha256", secret).update(text).digest("hex")}`;
}

function normalizeEmail(value: unknown): string | null {
  const text = typeof value === "string" ? value.trim().toLowerCase() : "";
  return AUTH_ACTIVITY_EMAIL_PATTERN.test(text) ? text : null;
}

function authActivityRequest(c: Context) {
  const forwardedFor = c.req.header("x-forwarded-for")?.split(",")[0]?.trim();
  const ipAddress = c.req.header("cf-connecting-ip") ?? c.req.header("x-real-ip") ?? forwardedFor ?? null;
  const userAgent = c.req.header("user-agent") ?? null;
  const path = (() => {
    try {
      return new URL(c.req.url).pathname;
    } catch {
      return c.req.path;
    }
  })();
  return {
    requestId: c.req.header("x-request-id") ?? c.req.header("x-gencow-request-id") ?? null,
    ipAddress,
    ipHash: authActivityHash(ipAddress),
    userAgent,
    userAgentHash: authActivityHash(userAgent),
    country: c.req.header("cf-ipcountry") ?? c.req.header("x-vercel-ip-country") ?? null,
    method: c.req.method,
    path,
  };
}

function sanitizeAuthActivityMetadata(
  metadata: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const output: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(metadata ?? {})) {
    if (!AUTH_ACTIVITY_METADATA_ALLOWLIST.has(key) || AUTH_ACTIVITY_SECRET_KEY_PATTERN.test(key)) continue;
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (!text) continue;
    output[key] = AUTH_ACTIVITY_EMAIL_PATTERN.test(text)
      ? "[email]"
      : String(redactAuthLogValue(text)).slice(0, 256);
  }
  return output;
}

function rowsFromResult(result: unknown): any[] {
  if (Array.isArray(result)) return result;
  const rows = (result as { rows?: unknown })?.rows;
  return Array.isArray(rows) ? rows : [];
}

async function loadAuthUserById(dbInstance: any, userId: string | null): Promise<AuthUserSnapshot | null> {
  if (!userId || !dbInstance || typeof dbInstance.execute !== "function") return null;
  try {
    const result = await dbInstance.execute(sql`
      SELECT id, email, name, email_verified
      FROM "user"
      WHERE id = ${userId}
      LIMIT 1
    `);
    const row = rowsFromResult(result)[0];
    if (!row?.id) return null;
    return {
      id: String(row.id),
      email: String(row.email ?? ""),
      name: row.name ? String(row.name) : null,
      role: null,
      plan: null,
      emailVerified: Boolean(row.email_verified),
    };
  } catch {
    return null;
  }
}

async function loadAuthUserByEmail(dbInstance: any, email: string | null): Promise<AuthUserSnapshot | null> {
  if (!email || !dbInstance || typeof dbInstance.execute !== "function") return null;
  try {
    const result = await dbInstance.execute(sql`
      SELECT id, email, name, email_verified
      FROM "user"
      WHERE lower(email) = ${email}
      LIMIT 1
    `);
    const row = rowsFromResult(result)[0];
    if (!row?.id) return null;
    return {
      id: String(row.id),
      email: String(row.email ?? ""),
      name: row.name ? String(row.name) : null,
      role: null,
      plan: null,
      emailVerified: Boolean(row.email_verified),
    };
  } catch {
    return null;
  }
}

async function buildAuthActivityEvent(
  dbInstance: any,
  c: Context,
  params: AuthActivityParams,
): Promise<AuthActivityEvent> {
  const request = authActivityRequest(c);
  const actorUser = await loadAuthUserById(dbInstance, params.actorUserId ?? null);
  const attemptedEmail = normalizeEmail(params.authAttempt?.attemptedEmail);
  const matchedUser =
    params.authAttempt?.matchedUserId
      ? await loadAuthUserById(dbInstance, params.authAttempt.matchedUserId)
      : await loadAuthUserByEmail(dbInstance, attemptedEmail);
  const actorType = (params.actorType ?? (params.actorUserId ? "user" : "anonymous")).slice(0, 64);
  const actorEmail = normalizeEmail(actorUser?.email);
  const attemptedEmailHash = authActivityHash(attemptedEmail);
  const actorEmailHash = authActivityHash(actorEmail);
  const authAttemptJson = {
    attemptedEmail,
    attemptedEmailHash,
    matchedUserId: params.authAttempt?.matchedUserId ?? matchedUser?.id ?? null,
    provider: params.authAttempt?.provider ?? "email",
    reasonCode: params.reasonCode ?? null,
  };
  const actorUserSnapshotJson = actorUser
    ? {
        id: actorUser.id,
        email: actorUser.email,
        name: actorUser.name,
        role: actorUser.role,
        plan: actorUser.plan,
        emailVerified: actorUser.emailVerified,
      }
    : {};
  const requestContextJson = {
    requestId: request.requestId,
    ipAddress: request.ipAddress,
    ipHash: request.ipHash,
    userAgent: request.userAgent,
    userAgentHash: request.userAgentHash,
    country: request.country,
    method: request.method,
    path: request.path,
    status: params.httpStatus ?? null,
  };
  const metadataJson = sanitizeAuthActivityMetadata(params.metadata);
  return {
    eventId: `auth_${randomUUID()}`,
    occurredAt: new Date(),
    receivedAt: new Date(),
    category: "auth",
    action: params.action.slice(0, 96),
    outcome: params.outcome,
    severity: params.outcome === "success" ? "info" : "warning",
    actorType,
    actorUserId: params.actorUserId ?? null,
    actorRole: null,
    actorSessionIdHash: authActivityHash(params.actorSessionToken),
    actorTokenId: null,
    targetType: null,
    targetId: null,
    targetOwnerUserId: null,
    appId: null,
    appName: null,
    environment: null,
    deployId: null,
    requestId: request.requestId,
    source: "server.auth",
    ipHash: request.ipHash,
    userAgentHash: request.userAgentHash,
    country: request.country,
    reasonCode: params.reasonCode ?? null,
    metadataJson,
    searchText: ["auth", params.action, params.outcome, params.actorUserId, params.reasonCode]
      .filter(Boolean)
      .join(" ")
      .slice(0, 1024),
    actorUserSnapshotJson,
    targetUserSnapshotJson: {},
    appSnapshotJson: {},
    requestContextJson,
    authAttemptJson,
    deployContextJson: {},
    contextStatus: actorUser ? "full" : actorType === "anonymous" ? "anonymous" : "partial",
    actorEmail,
    actorEmailHash,
    targetUserEmail: null,
    targetUserEmailHash: null,
    attemptedEmail,
    attemptedEmailHash,
    ownerEmail: null,
    ownerEmailHash: null,
    appOwnerUserId: null,
    routePath: request.path,
    httpMethod: request.method,
    httpStatus: params.httpStatus ?? null,
  };
}

async function appendAuthActivitySpool(event: AuthActivityEvent): Promise<void> {
  const { mkdir, open } = await import("node:fs/promises");
  const { join } = await import("node:path");
  const dir = process.env.GENCOW_ACTIVITY_SPOOL_DIR ?? AUTH_ACTIVITY_SPOOL_DIR;
  await mkdir(dir, { recursive: true, mode: 0o700 });
  const file = join(dir, `${new Date().toISOString().slice(0, 10)}.jsonl`);
  const handle = await open(file, "a", 0o600);
  try {
    await handle.appendFile(`${JSON.stringify(event)}\n`, "utf8");
  } finally {
    await handle.close();
  }
}

export async function recordAuthActivity(
  dbInstance: any,
  c: Context,
  params: AuthActivityParams,
): Promise<void> {
  const event = await buildAuthActivityEvent(dbInstance, c, params);
  try {
    if (!dbInstance || typeof dbInstance.execute !== "function") {
      throw new Error("auth activity DB handle is unavailable");
    }
    await dbInstance.execute(sql`
      INSERT INTO platform_activity_events (
        event_id, category, action, outcome, severity, actor_type, actor_user_id,
        actor_session_id_hash, request_id, source, ip_hash, user_agent_hash, country,
        reason_code, metadata_json, search_text, actor_user_snapshot_json,
        target_user_snapshot_json, app_snapshot_json, request_context_json, auth_attempt_json,
        deploy_context_json, context_status, actor_email, actor_email_hash, target_user_email,
        target_user_email_hash, attempted_email, attempted_email_hash, owner_email,
        owner_email_hash, app_owner_user_id, route_path, http_method, http_status
      ) VALUES (
        ${event.eventId}, 'auth', ${event.action}, ${event.outcome},
        ${event.severity},
        ${event.actorType},
        ${event.actorUserId},
        ${event.actorSessionIdHash},
        ${event.requestId},
        'server.auth',
        ${event.ipHash},
        ${event.userAgentHash},
        ${event.country},
        ${event.reasonCode},
        ${JSON.stringify(event.metadataJson)}::jsonb,
        ${event.searchText},
        ${JSON.stringify(event.actorUserSnapshotJson)}::jsonb,
        ${JSON.stringify(event.targetUserSnapshotJson)}::jsonb,
        ${JSON.stringify(event.appSnapshotJson)}::jsonb,
        ${JSON.stringify(event.requestContextJson)}::jsonb,
        ${JSON.stringify(event.authAttemptJson)}::jsonb,
        ${JSON.stringify(event.deployContextJson)}::jsonb,
        ${event.contextStatus},
        ${event.actorEmail},
        ${event.actorEmailHash},
        ${event.targetUserEmail},
        ${event.targetUserEmailHash},
        ${event.attemptedEmail},
        ${event.attemptedEmailHash},
        ${event.ownerEmail},
        ${event.ownerEmailHash},
        ${event.appOwnerUserId},
        ${event.routePath},
        ${event.httpMethod},
        ${event.httpStatus}
      )
    `);
  } catch (error) {
    try {
      await appendAuthActivitySpool(event);
    } catch (spoolError) {
      console.warn("[activity-log] auth event dropped:", {
        insert: redactAuthLogValue(error),
        spool: redactAuthLogValue(spoolError),
      });
    }
  }
}
